import React, { useEffect, useState } from "react";
import { ChevronDown, Code2, ImageUp, Save, Settings2, Trash2, UploadCloud } from "lucide-react";
import AdminLayout from "./AdminLayout";
import API, { getMediaUrl } from "../services/api";

const AVIATOR_RATIO_LABELS = [
  "1.01-1.09",
  "1.10-2",
  "2.01-5",
  "5.01-10",
  "10.01-20",
  "20.01-50",
  "50.01-100",
  "100.01-220",
  "220.01-500",
];

const ADMIN_PAGE_CHOICES = [
  { path: "/admin", label: "Dashboard" },
  { path: "/admin/users", label: "Users" },
  { path: "/admin/deposits", label: "Deposits" },
  { path: "/admin/payouts", label: "Payouts" },
  { path: "/admin/pnl-statement", label: "P&L Statement" },
  { path: "/admin/expenses", label: "Expenses" },
  { path: "/admin/referral-bonuses", label: "Referral Bonus" },
  { path: "/admin/results", label: "Market Outcome" },
  { path: "/admin/auto-results", label: "Auto Result Demo" },
  { path: "/admin/markets", label: "Game Management" },
  { path: "/admin/charts", label: "Chart Upload" },
  { path: "/admin/banners", label: "Banners" },
  { path: "/admin/payment-gateway", label: "Payment Gateway" },
  { path: "/admin/notifications", label: "Send Notifications" },
  { path: "/admin/agents", label: "Agent Program" },
  { path: "/admin/casino", label: "MK Casino" },
  { path: "/admin/promotions", label: "Promotions" },
  { path: "/admin/settings", label: "Settings" },
  { path: "/admin/coding", label: "Coding" },
  { path: "/admin/google-auth", label: "Google Auth" },
  { path: "/admin/sub-admins", label: "Sub Admins" },
  { path: "/admin/starline/declare", label: "Starline" },
];

const defaultForm = {
  aviator_enabled: "1",
  aviator_history_limit: "40",
  aviator_max_round_limit: "500",
  aviator_ratio_weights: currentRatioDefault(),
  aviator_ratio_profiles: '[{"id":"row_1","name":"Row 1","weights":[16,40,20,12,7,2.7,1.1,0.5,0.5]}]',
  aviator_active_ratio_profile: "row_1",
  aviator_ratio_switch_mode: "manual",
  aviator_ratio_switch_minutes: "10",
  admin_page_order: "",
  admin_hidden_pages: "",
  asset_whatsapp_logo_url: "/whatsapp-logo.svg",
  asset_starline_logo_url: "/starline-card-logo-final-2.png",
  asset_color_game_logo_url: "/background-removed-background-removed.png",
  asset_aviator_logo_url: "/aviator_logo_5_transparent_outside_512.webp",
  deposit_quick_amounts: "200,300,500,1000,2000,5000",
  user_deposit_quick_amounts: "200,500,1000,5000,10000",
  agent_section_enabled: "1",
  market_open_result_reveal_minutes: "30",
  market_outcome_reveal_minutes: "10",
  market_upcoming_result_window_minutes: "30",
  market_pending_result_delay_minutes: "10",
  market_day_market_cutoff_hour: "18",
  market_result_day_reset_hour: "2",
  market_next_day_bet_start_hour: "0",
  sidebar_profit_note: "Work hard",
  payment_gateway_provider: "",
  payment_gateway_mode: "manual",
  payment_gateway_api_base_url: "",
  payment_gateway_merchant_id: "",
  payment_gateway_callback_url: "",
  payment_gateway_code_snippet: "",
  otp_provider: "",
  otp_api_base_url: "",
  otp_api_key: "",
  otp_sender_id: "",
  otp_template_id: "",
  otp_code_snippet: "",
  site_ops_notes: "",
};

const assetFields = [
  { key: "asset_whatsapp_logo_url", label: "WhatsApp Logo", fallback: "/whatsapp-logo.svg" },
  { key: "asset_starline_logo_url", label: "Starline Logo", fallback: "/starline-card-logo-final-2.png" },
  { key: "asset_color_game_logo_url", label: "Color Game Logo", fallback: "/background-removed-background-removed.png" },
  { key: "asset_aviator_logo_url", label: "Aviator Logo", fallback: "/aviator_logo_5_transparent_outside_512.webp" },
];

function AdminHeavySettings() {
  const [form, setForm] = useState(defaultForm);
  const [aviatorProfiles, setAviatorProfiles] = useState(createDefaultAviatorProfiles());
  const [activeAviatorRatioProfile, setActiveAviatorRatioProfile] = useState("row_1");
  const [aviatorRatioCheck, setAviatorRatioCheck] = useState({ ok: true, message: "" });
  const [pageOrderKeys, setPageOrderKeys] = useState(ADMIN_PAGE_CHOICES.map((item) => item.path));
  const [storageStats, setStorageStats] = useState({ total_deposit_requests: 0, total_deposit_screenshots: 0 });
  const [resetForm, setResetForm] = useState({
    mode: "bets",
    date_from: new Date().toISOString().slice(0, 10),
    date_to: new Date().toISOString().slice(0, 10),
    user_id: "",
    confirm_password: "",
  });
  const [saving, setSaving] = useState(false);
  const [uploadingKey, setUploadingKey] = useState("");
  const hiddenPageKeys = parsePageKeyList(form.admin_hidden_pages);

  useEffect(() => {
    API.get("/admin/settings")
      .then((res) => {
        const data = res.data || {};
        setForm((current) => ({ ...current, ...data }));
        const profiles = parseStoredAviatorProfiles(data.aviator_ratio_profiles, data.aviator_ratio_weights || currentRatioDefault());
        setAviatorProfiles(profiles);
        setActiveAviatorRatioProfile(String(data.aviator_active_ratio_profile || profiles[0]?.id || "row_1"));
        const savedOrder = String(data.admin_page_order || "").trim().split(",").map((item) => item.trim()).filter(Boolean);
        if (savedOrder.length) {
          const remainder = ADMIN_PAGE_CHOICES.map((item) => item.path).filter((path) => !savedOrder.includes(path));
          setPageOrderKeys([...savedOrder.filter((path) => ADMIN_PAGE_CHOICES.some((item) => item.path === path)), ...remainder]);
        }
      })
      .catch(() => {});

    API.get("/admin/system/storage-stats")
      .then((res) => setStorageStats(res.data || { total_deposit_requests: 0, total_deposit_screenshots: 0 }))
      .catch(() => {});
  }, []);

  const saveSettings = async () => {
    const activeProfile = aviatorProfiles.find((item) => item.id === activeAviatorRatioProfile) || aviatorProfiles[0];
    const ratioValidation = validateAviatorRatioValues(activeProfile?.weights || []);
    setAviatorRatioCheck(ratioValidation);
    if (!ratioValidation.ok) {
      alert(ratioValidation.message);
      return;
    }

    const serializedProfiles = aviatorProfiles.map((item) => {
      const validated = validateAviatorRatioValues(item.weights || []);
      return {
        id: item.id,
        name: item.name || "Row",
        weights: validated.ok ? validated.values : normalizeAviatorProfileWeights(item.weights || []),
      };
    });

    setSaving(true);
    try {
      await API.post("/admin/settings", {
        ...form,
        aviator_ratio_profiles: JSON.stringify(serializedProfiles),
        aviator_active_ratio_profile: activeProfile?.id || "row_1",
        aviator_ratio_weights: ratioValidation.values.join(","),
        admin_page_order: pageOrderKeys.join(","),
        admin_hidden_pages: hiddenPageKeys.join(","),
      });
      alert("Heavy settings saved");
    } catch (err) {
      alert(err?.response?.data?.message || "Unable to save heavy settings");
    } finally {
      setSaving(false);
    }
  };

  const uploadAsset = async (settingKey, file) => {
    if (!file) return;
    const formData = new FormData();
    formData.append("asset", file);
    formData.append("setting_key", settingKey);
    setUploadingKey(settingKey);
    try {
      const res = await API.post("/admin/site-assets/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const url = res.data?.url || "";
      if (url) {
        setForm((current) => ({ ...current, [settingKey]: url }));
      }
      alert("Logo uploaded");
    } catch (err) {
      alert(err?.response?.data?.message || "Unable to upload logo");
    } finally {
      setUploadingKey("");
    }
  };

  const movePageOrder = (index, direction) => {
    setPageOrderKeys((current) => {
      const next = [...current];
      const target = index + direction;
      if (target < 0 || target >= next.length) return current;
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const toggleHiddenPage = (pathKey) => {
    setForm((current) => {
      const currentKeys = parsePageKeyList(current.admin_hidden_pages);
      const nextKeys = currentKeys.includes(pathKey)
        ? currentKeys.filter((item) => item !== pathKey)
        : [...currentKeys, pathKey];
      return { ...current, admin_hidden_pages: nextKeys.join(",") };
    });
  };

  const updateAviatorProfile = (profileId, patch) => {
    setAviatorProfiles((current) => current.map((item) => item.id === profileId ? { ...item, ...patch } : item));
  };

  const updateAviatorWeight = (profileId, index, rawValue) => {
    const cleaned = String(rawValue || "").replace(/[^0-9.]/g, "");
    setAviatorProfiles((current) => current.map((item) => {
      if (item.id !== profileId) return item;
      const nextWeights = Array.isArray(item.weights) ? [...item.weights] : normalizeAviatorProfileWeights([]);
      nextWeights[index] = cleaned === "" ? "" : cleaned;
      return { ...item, weights: nextWeights };
    }));
    if (activeAviatorRatioProfile === profileId && aviatorRatioCheck.message) {
      setAviatorRatioCheck({ ok: true, message: "" });
    }
  };

  const addAviatorProfile = () => {
    setAviatorProfiles((current) => {
      const nextIndex = current.length + 1;
      return [
        ...current,
        { id: `row_${Date.now()}`, name: `Row ${nextIndex}`, weights: parseAviatorRatioDraft(currentRatioDefault()) },
      ];
    });
  };

  const removeAviatorProfile = (profileId) => {
    setAviatorProfiles((current) => {
      if (current.length <= 1) return current;
      const next = current.filter((item) => item.id !== profileId);
      if (activeAviatorRatioProfile === profileId) {
        setActiveAviatorRatioProfile(next[0]?.id || "row_1");
      }
      return next;
    });
  };

  const checkAviatorRatio = () => {
    const activeProfile = aviatorProfiles.find((item) => item.id === activeAviatorRatioProfile) || aviatorProfiles[0];
    const result = validateAviatorRatioValues(activeProfile?.weights || []);
    setAviatorRatioCheck(result);
    alert(result.ok ? `${activeProfile?.name || "Selected row"} is valid.\n${result.values.join(", ")}` : result.message);
  };

  const resetRecords = async (modeOverride = "") => {
    const selectedMode = modeOverride || resetForm.mode;
    if (!resetForm.date_from || !resetForm.date_to || !resetForm.confirm_password) {
      alert("Select date range and enter admin password");
      return;
    }
    const labels = {
      bets: "bets",
      deposits: "deposits",
      withdraws: "withdraws",
      screenshots: "deposit screenshots",
      aviator_history: "aviator history",
      all_history: "all bets, P&L, deposits and withdraw history",
    };
    const label = labels[selectedMode] || "records";
    if (!window.confirm(`Clean ${label} records from ${resetForm.date_from} to ${resetForm.date_to}? Wallet balances will not be changed.`)) {
      return;
    }
    try {
      const res = await API.post("/admin/system/reset-records", {
        ...resetForm,
        mode: selectedMode,
        user_id: resetForm.user_id ? Number(resetForm.user_id) : null,
      });
      const deleted = res.data?.deleted || {};
      API.get("/admin/system/storage-stats")
        .then((statsRes) => setStorageStats(statsRes.data || { total_deposit_requests: 0, total_deposit_screenshots: 0 }))
        .catch(() => {});
      alert(`Cleanup done. Removed: ${Object.entries(deleted).map(([key, value]) => `${key} ${value}`).join(", ") || "0"}`);
      setResetForm((current) => ({ ...current, confirm_password: "" }));
    } catch (err) {
      alert(err?.response?.data?.message || "Unable to reset records");
    }
  };

  return (
    <AdminLayout>
      <section style={page} className="admin-coding-page">
        <style>{codingResponsiveCss}</style>
        <div style={header}>
          <div>
            <h1 style={title}>Coding</h1>
            <p style={subtitle}>Aviator, cleanup, page order, logos, deposit quick amounts, agent section, payment API and OTP API controls.</p>
          </div>
          <button type="button" style={saveButton} onClick={saveSettings} disabled={saving}>
            <Save size={18} /> {saving ? "Saving..." : "Save All"}
          </button>
        </div>

        <section style={quickGrid}>
          <QuickCard title="Aviator" value={String(form.aviator_enabled) === "1" ? "Enabled" : "Disabled"} />
          <QuickCard title="Admin Pages" value={`${pageOrderKeys.length} items`} />
          <QuickCard title="Hidden Pages" value={`${hiddenPageKeys.length} hidden`} />
          <QuickCard title="Deposit Shots" value={String(storageStats.total_deposit_screenshots || 0)} />
          <QuickCard title="Agent Section" value={String(form.agent_section_enabled || "1") === "1" ? "On" : "Off"} />
        </section>

        <Panel title="Admin Manual Deposit Quick Select" icon={<Settings2 size={16} />} defaultOpen>
          <Field
            label="Quick Amounts"
            value={form.deposit_quick_amounts}
            onChange={(value) => setForm((c) => ({ ...c, deposit_quick_amounts: value.replace(/[^0-9,\n\r ]/g, "") }))}
          />
          <div style={amountPreviewRow}>
            {parseQuickAmounts(form.deposit_quick_amounts).map((value) => (
              <span key={value} style={amountPreviewChip}>Rs {value}</span>
            ))}
          </div>
          <p style={helperText}>Used in admin Users and User Profile wallet deposit modals. Use comma or new line: 200,300,500,1000,2000,5000</p>
        </Panel>

        <Panel title="User Add Money Controls" icon={<Settings2 size={16} />} defaultOpen>
          <div style={grid}>
            <Field
              label="User Quick Amounts"
              value={form.user_deposit_quick_amounts}
              onChange={(value) => setForm((c) => ({ ...c, user_deposit_quick_amounts: value.replace(/[^0-9,\n\r ]/g, "") }))}
            />
            <FieldSelect
              label="Show User Agent Section"
              value={String(form.agent_section_enabled || "1")}
              onChange={(value) => setForm((c) => ({ ...c, agent_section_enabled: value }))}
              options={[
                { value: "1", label: "On" },
                { value: "0", label: "Off" },
              ]}
            />
          </div>
          <div style={amountPreviewRow}>
            {parseQuickAmounts(form.user_deposit_quick_amounts).map((value) => (
              <span key={value} style={amountPreviewChip}>Rs {value}</span>
            ))}
          </div>
          <p style={helperText}>User Quick Amounts controls the Add Money page buttons. Agent Section hides or shows assigned agent contact blocks for users.</p>
        </Panel>

        <Panel title="Market Outcome Timing" icon={<Settings2 size={16} />} defaultOpen>
          <div style={grid}>
            <Field
              label="Open Result Appears Before Open (Minutes)"
              value={form.market_open_result_reveal_minutes}
              onChange={(value) => setForm((c) => ({ ...c, market_open_result_reveal_minutes: cleanNumberText(value, 4) }))}
            />
            <Field
              label="Close Result Appears Before Close (Minutes)"
              value={form.market_outcome_reveal_minutes}
              onChange={(value) => setForm((c) => ({ ...c, market_outcome_reveal_minutes: cleanNumberText(value, 4) }))}
            />
            <Field
              label="Upcoming Box Window (Minutes)"
              value={form.market_upcoming_result_window_minutes}
              onChange={(value) => setForm((c) => ({ ...c, market_upcoming_result_window_minutes: cleanNumberText(value, 4) }))}
            />
            <Field
              label="Pending Count After Result Time (Minutes)"
              value={form.market_pending_result_delay_minutes}
              onChange={(value) => setForm((c) => ({ ...c, market_pending_result_delay_minutes: cleanNumberText(value, 4) }))}
            />
            <Field
              label="Day Market Cutoff Hour (0-23)"
              value={form.market_day_market_cutoff_hour}
              onChange={(value) => setForm((c) => ({ ...c, market_day_market_cutoff_hour: cleanNumberText(value, 2) }))}
            />
            <Field
              label="Result Date Reset Hour (0-23)"
              value={form.market_result_day_reset_hour}
              onChange={(value) => setForm((c) => ({ ...c, market_result_day_reset_hour: cleanNumberText(value, 2) }))}
            />
            <Field
              label="Next Day Bet Start Hour (0-23)"
              value={form.market_next_day_bet_start_hour}
              onChange={(value) => setForm((c) => ({ ...c, market_next_day_bet_start_hour: cleanNumberText(value, 2) }))}
            />
          </div>
          <p style={helperText}>Defaults: open appears 30 minutes before open, close appears 10 minutes before close, upcoming box looks 30 minutes ahead, pending count starts 10 minutes after result time, and before 6 PM only day markets are shown. Reset hour 2 keeps admin results on yesterday until 2 AM.</p>
        </Panel>

        <Panel title="Sidebar Today Profit" icon={<Settings2 size={16} />} defaultOpen>
          <Field
            label="Motivation Line"
            value={form.sidebar_profit_note}
            onChange={(value) => setForm((c) => ({ ...c, sidebar_profit_note: value }))}
          />
          <p style={helperText}>Shown under Today Profit in the admin sidebar for main admin and sub-admins.</p>
        </Panel>

        <Panel title="Aviator Round Setting" icon={<Settings2 size={16} />} defaultOpen>
          <div style={grid}>
            <FieldSelect
              label="Aviator Under MK Casino"
              value={String(form.aviator_enabled || "1")}
              onChange={(value) => setForm((c) => ({ ...c, aviator_enabled: value }))}
              options={[
                { value: "1", label: "Enabled" },
                { value: "0", label: "Disabled" },
              ]}
            />
            <Field label="Aviator History Limit" value={form.aviator_history_limit} onChange={(value) => setForm((c) => ({ ...c, aviator_history_limit: value.replace(/[^\d]/g, "").slice(0, 3) }))} />
            <Field label="Aviator Max Round Limit" value={form.aviator_max_round_limit} onChange={(value) => setForm((c) => ({ ...c, aviator_max_round_limit: value.replace(/[^\d]/g, "").slice(0, 3) }))} />
            <FieldSelect
              label="Ratio Switch Mode"
              value={String(form.aviator_ratio_switch_mode || "manual")}
              onChange={(value) => setForm((c) => ({ ...c, aviator_ratio_switch_mode: value }))}
              options={[
                { value: "manual", label: "Manual Select" },
                { value: "random", label: "Auto Random Switch" },
              ]}
            />
            <Field label="Auto Switch Minutes" value={form.aviator_ratio_switch_minutes} onChange={(value) => setForm((c) => ({ ...c, aviator_ratio_switch_minutes: value.replace(/[^\d]/g, "").slice(0, 3) || "0" }))} />
          </div>

          <div style={ratioProfilesWrap}>
            {aviatorProfiles.map((profile, index) => (
              <div key={profile.id} style={profileCard(activeAviatorRatioProfile === profile.id)}>
                <div style={profileHeader}>
                  <label style={profileRadioWrap}>
                    <input
                      type="radio"
                      name="aviator_active_profile"
                      checked={activeAviatorRatioProfile === profile.id}
                      onChange={() => setActiveAviatorRatioProfile(profile.id)}
                    />
                    <span>Use This Row</span>
                  </label>
                  <button type="button" style={removeProfileButton} onClick={() => removeAviatorProfile(profile.id)} disabled={aviatorProfiles.length <= 1}>Remove</button>
                </div>
                <input
                  style={profileNameInput}
                  value={profile.name}
                  onChange={(event) => updateAviatorProfile(profile.id, { name: event.target.value })}
                  placeholder={`Row ${index + 1}`}
                />
                <div style={profileWeightsRow}>
                  {AVIATOR_RATIO_LABELS.map((label, weightIndex) => (
                    <label key={`${profile.id}-${label}`} style={ratioMiniField}>
                      <span style={ratioMiniLabel}>{label}</span>
                      <input
                        style={ratioMiniInput}
                        value={profile.weights?.[weightIndex] ?? ""}
                        onChange={(event) => updateAviatorWeight(profile.id, weightIndex, event.target.value)}
                        placeholder="0"
                      />
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div style={actionRow}>
            <button type="button" style={secondaryButton} onClick={addAviatorProfile}>Add New Row</button>
            <button type="button" style={darkButton} onClick={checkAviatorRatio}>Check Selected Row</button>
            <span style={aviatorRatioCheck.ok ? okText : errorText}>{aviatorRatioCheck.message || "Selected row will become live after Save All."}</span>
          </div>
        </Panel>

        <Panel title="Admin Page Order" icon={<ChevronDown size={16} />}>
          <p style={helperText}>This list stays hidden until you open it. Use Up/Down for sidebar order and Hide for pages you do not want in the normal sidebar.</p>
          <div style={orderList}>
            {pageOrderKeys.map((pathKey, index) => {
              const item = ADMIN_PAGE_CHOICES.find((entry) => entry.path === pathKey);
              if (!item) return null;
              const hidden = hiddenPageKeys.includes(pathKey);
              return (
                <div key={pathKey} style={hidden ? hiddenOrderRow : orderRow}>
                  <span style={orderIndex}>{index + 1}</span>
                  <strong style={orderLabel}>{item.label}{hidden ? <span style={hiddenOrderBadge}>Hidden</span> : null}</strong>
                  <div style={orderButtons}>
                    <button type="button" style={orderButton} onClick={() => movePageOrder(index, -1)} disabled={index === 0}>Up</button>
                    <button type="button" style={orderButton} onClick={() => movePageOrder(index, 1)} disabled={index === pageOrderKeys.length - 1}>Down</button>
                    <button type="button" style={hidden ? unhideButton : hideButton} onClick={() => toggleHiddenPage(pathKey)}>
                      {hidden ? "Unhide" : "Hide"}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </Panel>

        <Panel title="Logo And Game Assets" icon={<ImageUp size={16} />} defaultOpen>
          <div style={assetGrid}>
            {assetFields.map((asset) => (
              <div key={asset.key} style={assetCard}>
                <div style={assetPreview}>
                  <img src={resolveAssetPreview(form[asset.key], asset.fallback)} alt={asset.label} style={assetImage} />
                </div>
                <Field label={asset.label} value={form[asset.key]} onChange={(value) => setForm((c) => ({ ...c, [asset.key]: value }))} />
                <label style={uploadButton}>
                  <UploadCloud size={16} />
                  {uploadingKey === asset.key ? "Uploading..." : "Upload"}
                  <input
                    type="file"
                    accept="image/*"
                    style={hiddenInput}
                    disabled={Boolean(uploadingKey)}
                    onChange={(event) => {
                      uploadAsset(asset.key, event.target.files?.[0]);
                      event.target.value = "";
                    }}
                  />
                </label>
              </div>
            ))}
          </div>
        </Panel>

        <Panel title="Payment API Setup" icon={<Code2 size={16} />}>
          <div style={grid}>
            <Field label="Provider Name" value={form.payment_gateway_provider} onChange={(value) => setForm((c) => ({ ...c, payment_gateway_provider: value }))} />
            <FieldSelect
              label="Mode"
              value={form.payment_gateway_mode || "manual"}
              onChange={(value) => setForm((c) => ({ ...c, payment_gateway_mode: value }))}
              options={[
                { value: "manual", label: "Manual / QR" },
                { value: "sandbox", label: "Sandbox" },
                { value: "live", label: "Live Gateway" },
              ]}
            />
            <Field label="API Base URL" value={form.payment_gateway_api_base_url} onChange={(value) => setForm((c) => ({ ...c, payment_gateway_api_base_url: value }))} />
            <Field label="Merchant ID" value={form.payment_gateway_merchant_id} onChange={(value) => setForm((c) => ({ ...c, payment_gateway_merchant_id: value }))} />
            <Field label="Callback URL" value={form.payment_gateway_callback_url} onChange={(value) => setForm((c) => ({ ...c, payment_gateway_callback_url: value }))} />
          </div>
          <TextArea label="Payment Gateway Code / Notes" value={form.payment_gateway_code_snippet} onChange={(value) => setForm((c) => ({ ...c, payment_gateway_code_snippet: value }))} />
        </Panel>

        <Panel title="OTP API Setup" icon={<Code2 size={16} />}>
          <div style={grid}>
            <Field label="OTP Provider" value={form.otp_provider} onChange={(value) => setForm((c) => ({ ...c, otp_provider: value }))} />
            <Field label="OTP API Base URL" value={form.otp_api_base_url} onChange={(value) => setForm((c) => ({ ...c, otp_api_base_url: value }))} />
            <Field label="OTP API Key" value={form.otp_api_key} onChange={(value) => setForm((c) => ({ ...c, otp_api_key: value }))} />
            <Field label="Sender ID" value={form.otp_sender_id} onChange={(value) => setForm((c) => ({ ...c, otp_sender_id: value }))} />
            <Field label="Template ID" value={form.otp_template_id} onChange={(value) => setForm((c) => ({ ...c, otp_template_id: value }))} />
          </div>
          <TextArea label="OTP Code / Notes" value={form.otp_code_snippet} onChange={(value) => setForm((c) => ({ ...c, otp_code_snippet: value }))} />
        </Panel>

        <Panel title="Site Ops Notes" icon={<Code2 size={16} />}>
          <TextArea label="Admin Work Notes / Future AWS Commands" value={form.site_ops_notes} onChange={(value) => setForm((c) => ({ ...c, site_ops_notes: value }))} />
        </Panel>

        <Panel title="Date Wise Data Cleanup" icon={<Trash2 size={16} />}>
          <div style={statsStrip}>
            <span>Total deposit requests: <strong>{storageStats.total_deposit_requests || 0}</strong></span>
            <span>Total screenshots: <strong>{storageStats.total_deposit_screenshots || 0}</strong></span>
          </div>
          <div style={grid}>
            <FieldSelect
              label="Cleanup Type"
              value={resetForm.mode}
              onChange={(value) => setResetForm((current) => ({ ...current, mode: value }))}
              options={[
                { value: "bets", label: "All Bets" },
                { value: "all_history", label: "All Bets + P&L + D/W" },
                { value: "deposits", label: "Deposits" },
                { value: "withdraws", label: "Withdraws" },
                { value: "screenshots", label: "Deposit Screenshots" },
                { value: "aviator_history", label: "Aviator History" },
              ]}
            />
            <Field label="User ID (optional)" value={resetForm.user_id} onChange={(value) => setResetForm((current) => ({ ...current, user_id: value.replace(/[^\d]/g, "") }))} />
            <Field label="From Date" type="date" value={resetForm.date_from} onChange={(value) => setResetForm((current) => ({ ...current, date_from: value }))} />
            <Field label="To Date" type="date" value={resetForm.date_to} onChange={(value) => setResetForm((current) => ({ ...current, date_to: value }))} />
          </div>
          <Field label="Admin Password" type="password" value={resetForm.confirm_password} onChange={(value) => setResetForm((current) => ({ ...current, confirm_password: value }))} />
          <div style={actionRow}>
            <button type="button" style={dangerButton} onClick={() => resetRecords()}>Clean Selected Records</button>
            <button type="button" style={dangerButtonWide} onClick={() => resetRecords("all_history")}>Clean All Bets + P&amp;L + D/W</button>
          </div>
        </Panel>
      </section>
    </AdminLayout>
  );
}

function Panel({ title, icon, defaultOpen = false, children }) {
  return (
    <details style={panel} open={defaultOpen}>
      <summary style={panelSummary}>
        <span style={panelTitle}>{icon}{title}</span>
        <ChevronDown size={16} />
      </summary>
      <div style={panelBody}>{children}</div>
    </details>
  );
}

function QuickCard({ title, value }) {
  return (
    <article style={quickCard}>
      <span>{title}</span>
      <strong>{value}</strong>
    </article>
  );
}

function Field({ label, value, onChange, type = "text" }) {
  return (
    <label style={fieldWrap}>
      <span style={fieldLabel}>{label}</span>
      <input type={type} style={fieldInput} value={value || ""} onChange={(event) => onChange(event.target.value)} />
    </label>
  );
}

function FieldSelect({ label, value, onChange, options }) {
  return (
    <label style={fieldWrap}>
      <span style={fieldLabel}>{label}</span>
      <select style={fieldInput} value={value} onChange={(event) => onChange(event.target.value)}>
        {options.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
      </select>
    </label>
  );
}

function TextArea({ label, value, onChange }) {
  return (
    <label style={fieldWrap}>
      <span style={fieldLabel}>{label}</span>
      <textarea style={textArea} value={value || ""} onChange={(event) => onChange(event.target.value)} />
    </label>
  );
}

function createDefaultAviatorProfiles() {
  return [
    { id: "row_1", name: "Row 1", weights: [16, 40, 20, 12, 7, 2.7, 1.1, 0.5, 0.5] },
  ];
}

function currentRatioDefault() {
  return "16,40,20,12,7,2.7,1.1,0.5,0.5";
}

function parseStoredAviatorProfiles(rawProfiles, rawFallbackWeights) {
  const fallback = parseAviatorRatioDraft(rawFallbackWeights || currentRatioDefault());
  try {
    const parsed = JSON.parse(String(rawProfiles || "[]"));
    if (!Array.isArray(parsed) || !parsed.length) throw new Error("empty");
    const next = parsed.map((item, index) => ({
      id: String(item?.id || `row_${index + 1}`),
      name: String(item?.name || `Row ${index + 1}`),
      weights: normalizeAviatorProfileWeights(Array.isArray(item?.weights) ? item.weights : parseAviatorRatioDraft(item?.weights || ""), fallback),
    }));
    return next.length ? next : createDefaultAviatorProfiles();
  } catch {
    return [{ id: "row_1", name: "Row 1", weights: fallback }];
  }
}

function parseAviatorRatioDraft(value = "") {
  return String(value || "")
    .split(/[,\n\r]+/)
    .map((item) => Number.parseFloat(String(item).trim()))
    .filter((item) => Number.isFinite(item) && item > 0);
}

function normalizeAviatorProfileWeights(weights, fallback = parseAviatorRatioDraft(currentRatioDefault())) {
  const values = Array.isArray(weights)
    ? weights.map((item) => Number.parseFloat(item)).filter((item) => Number.isFinite(item) && item > 0)
    : [];
  return values.length === AVIATOR_RATIO_LABELS.length ? values : [...fallback];
}

function validateAviatorRatioValues(rawValues) {
  const values = Array.isArray(rawValues)
    ? rawValues.map((item) => Number.parseFloat(item)).filter((item) => Number.isFinite(item) && item > 0)
    : [];
  if (values.length !== AVIATOR_RATIO_LABELS.length) {
    return { ok: false, message: `Need exactly ${AVIATOR_RATIO_LABELS.length} weights. Current: ${values.length}`, values };
  }
  if (values.some((item) => item <= 0)) {
    return { ok: false, message: "All Aviator ratio weights must be greater than 0", values };
  }
  return { ok: true, message: `Valid ratio for ${AVIATOR_RATIO_LABELS.length} crash tiers`, values };
}

function resolveAssetPreview(value, fallback) {
  const raw = String(value || fallback || "").trim();
  if (raw.startsWith("/uploads/")) return getMediaUrl(raw);
  return raw || fallback;
}

function parseQuickAmounts(value) {
  const parsed = String(value || "")
    .split(/[,\n\r ]+/)
    .map((item) => Number.parseInt(item, 10))
    .filter((item) => Number.isFinite(item) && item > 0);
  return Array.from(new Set(parsed)).slice(0, 12);
}

function parsePageKeyList(value) {
  return Array.from(new Set(
    String(value || "")
      .split(",")
      .map((item) => item.trim())
      .filter((item) => ADMIN_PAGE_CHOICES.some((entry) => entry.path === item))
  ));
}

function cleanNumberText(value, maxLength = 3) {
  return String(value || "").replace(/[^\d]/g, "").slice(0, maxLength);
}

const page = { display: "grid", gap: "18px" };
const header = { display: "flex", justifyContent: "space-between", alignItems: "center", gap: "14px", flexWrap: "wrap" };
const title = { margin: 0, color: "#0f172a", fontSize: "26px", fontWeight: 900 };
const subtitle = { margin: "6px 0 0", color: "#64748b", fontSize: "13px" };
const saveButton = { minHeight: "46px", padding: "0 18px", border: 0, borderRadius: "14px", background: "#2563eb", color: "#fff", fontWeight: 900, display: "flex", alignItems: "center", gap: "8px", cursor: "pointer" };
const quickGrid = { display: "grid", gridTemplateColumns: "repeat(5, minmax(0, 1fr))", gap: "12px" };
const quickCard = { minHeight: "82px", border: "1px solid #e2e8f0", background: "#fff", borderRadius: "16px", padding: "14px", display: "grid", gap: "8px", color: "#64748b", boxShadow: "0 10px 22px rgba(15,23,42,.04)" };
const panel = { background: "#fff", border: "1px solid #e2e8f0", borderRadius: "18px", overflow: "hidden", boxShadow: "0 10px 24px rgba(15,23,42,.04)" };
const panelSummary = { minHeight: "60px", padding: "0 18px", display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer", color: "#0f172a", fontWeight: 900 };
const panelTitle = { display: "inline-flex", alignItems: "center", gap: "8px" };
const panelBody = { padding: "0 18px 18px", display: "grid", gap: "16px" };
const grid = { display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "14px" };
const fieldWrap = { display: "grid", gap: "7px" };
const fieldLabel = { color: "#475569", fontSize: "12px", fontWeight: 800, textTransform: "uppercase", letterSpacing: ".03em" };
const fieldInput = { height: "48px", border: "1px solid #dbe4f0", borderRadius: "12px", padding: "0 13px", fontSize: "14px", fontWeight: 700, outline: 0, minWidth: 0 };
const textArea = { ...fieldInput, height: "150px", padding: "12px 13px", resize: "vertical", fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace", fontSize: "12px", lineHeight: 1.5 };
const helperText = { margin: 0, color: "#64748b", fontSize: "12px", lineHeight: 1.45 };
const ratioProfilesWrap = { display: "grid", gap: "14px" };
const profileCard = (active) => ({ border: active ? "2px solid #2563eb" : "1px solid #dbe3f0", borderRadius: "16px", padding: "14px", background: active ? "#f8fbff" : "#fff", display: "grid", gap: "12px" });
const profileHeader = { display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px" };
const profileRadioWrap = { display: "inline-flex", alignItems: "center", gap: "8px", fontSize: "13px", fontWeight: 800, color: "#0f172a" };
const removeProfileButton = { height: "34px", padding: "0 12px", border: "1px solid #fecaca", borderRadius: "10px", background: "#fff5f5", color: "#b91c1c", fontWeight: 700, cursor: "pointer" };
const profileNameInput = { height: "42px", border: "1px solid #cbd5e1", borderRadius: "12px", padding: "0 14px", fontSize: "14px", fontWeight: 700, maxWidth: "220px" };
const profileWeightsRow = { display: "grid", gridTemplateColumns: "repeat(9, minmax(66px, 1fr))", gap: "8px", alignItems: "end" };
const ratioMiniField = { display: "grid", gap: "5px" };
const ratioMiniLabel = { fontSize: "10px", fontWeight: 800, color: "#475569", textAlign: "center", lineHeight: 1.2 };
const ratioMiniInput = { height: "38px", border: "1px solid #cbd5e1", borderRadius: "10px", padding: "0 8px", fontSize: "13px", fontWeight: 700, textAlign: "center", minWidth: 0 };
const actionRow = { display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" };
const secondaryButton = { height: "42px", padding: "0 16px", border: "1px solid #cbd5e1", borderRadius: "12px", background: "#fff", color: "#0f172a", fontWeight: 800, cursor: "pointer" };
const darkButton = { height: "42px", padding: "0 16px", border: 0, borderRadius: "12px", background: "#0f172a", color: "#fff", fontWeight: 800, cursor: "pointer" };
const okText = { color: "#15803d", fontSize: "12px", fontWeight: 800 };
const errorText = { color: "#b91c1c", fontSize: "12px", fontWeight: 800 };
const orderList = { display: "grid", gap: "10px" };
const orderRow = { display: "grid", gridTemplateColumns: "44px 1fr 194px", gap: "12px", alignItems: "center", padding: "12px 14px", borderRadius: "14px", border: "1px solid #dbe4f0", background: "#f8fafc" };
const hiddenOrderRow = { ...orderRow, borderColor: "#bfdbfe", background: "#eff6ff" };
const orderIndex = { width: "34px", height: "34px", borderRadius: "50%", background: "#111827", color: "#fff", display: "grid", placeItems: "center", fontWeight: 800 };
const orderLabel = { color: "#0f172a", fontSize: "14px", display: "inline-flex", alignItems: "center", gap: "8px", flexWrap: "wrap" };
const hiddenOrderBadge = { minHeight: "22px", padding: "0 8px", borderRadius: "999px", background: "#dbeafe", color: "#1d4ed8", display: "inline-flex", alignItems: "center", fontSize: "10px", fontWeight: 900 };
const orderButtons = { display: "grid", gridTemplateColumns: "1fr 1fr 1.2fr", gap: "8px" };
const orderButton = { height: "38px", border: "1px solid #cbd5e1", borderRadius: "10px", background: "#fff", color: "#334155", fontWeight: 700, cursor: "pointer" };
const hideButton = { ...orderButton, borderColor: "#fed7aa", background: "#fff7ed", color: "#c2410c", fontWeight: 900 };
const unhideButton = { ...orderButton, borderColor: "#bbf7d0", background: "#ecfdf5", color: "#15803d", fontWeight: 900 };
const assetGrid = { display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: "14px" };
const assetCard = { border: "1px solid #e2e8f0", borderRadius: "16px", padding: "14px", display: "grid", gap: "12px", background: "#f8fafc" };
const assetPreview = { height: "118px", border: "1px dashed #cbd5e1", borderRadius: "14px", background: "#fff", display: "grid", placeItems: "center", overflow: "hidden" };
const assetImage = { width: "100%", height: "100%", objectFit: "contain" };
const uploadButton = { height: "42px", borderRadius: "12px", border: 0, background: "#16a34a", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", fontWeight: 900, cursor: "pointer" };
const hiddenInput = { display: "none", width: 0, height: 0, minHeight: 0, padding: 0, border: 0 };
const amountPreviewRow = { display: "flex", flexWrap: "wrap", gap: "8px" };
const amountPreviewChip = { minHeight: "32px", padding: "0 12px", borderRadius: "999px", background: "#eff6ff", color: "#1d4ed8", border: "1px solid #bfdbfe", display: "inline-flex", alignItems: "center", fontSize: "12px", fontWeight: 900 };
const statsStrip = { display: "flex", gap: "14px", flexWrap: "wrap", padding: "10px 12px", borderRadius: "14px", background: "#fff7f7", border: "1px solid #fecaca", color: "#7f1d1d", fontSize: "12px", fontWeight: 700 };
const dangerButton = { ...darkButton, background: "#dc2626" };
const dangerButtonWide = { ...darkButton, background: "linear-gradient(135deg, #b91c1c, #7f1d1d)" };

const codingResponsiveCss = `
  .admin-coding-page input,
  .admin-coding-page select,
  .admin-coding-page textarea,
  .admin-coding-page button {
    max-width: 100%;
  }
  .admin-coding-page input[type="file"] {
    display: none !important;
    width: 0 !important;
    height: 0 !important;
    min-height: 0 !important;
    padding: 0 !important;
    border: 0 !important;
    opacity: 0 !important;
    pointer-events: none !important;
  }
  @media (max-width: 900px) {
    .admin-coding-page [style*="repeat(4"],
    .admin-coding-page [style*="repeat(2"],
    .admin-coding-page [style*="1fr 1fr"],
    .admin-coding-page [style*="44px 1fr 124px"],
    .admin-coding-page [style*="44px 1fr 194px"] {
      grid-template-columns: 1fr !important;
    }
    .admin-coding-page [style*="repeat(9"] {
      grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
    }
    .admin-coding-page label {
      min-width: 0 !important;
    }
    .admin-coding-page input:not([type="file"]),
    .admin-coding-page select,
    .admin-coding-page textarea {
      width: 100% !important;
      min-width: 0 !important;
    }
  }
  @media (max-width: 520px) {
    .admin-coding-page [style*="repeat(9"] {
      grid-template-columns: 1fr !important;
    }
  }
`;

export default AdminHeavySettings;
